import { Box, Modal, Tabs, TabPanels } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { UnderwritingNotes } from 'types';

import getFriendlyNameForReportType from '../helpers/getFriendlyNameForReportType';
import { useLazyGetCreditReportDetailsQuery } from '../services/creditReports';
import { CreditReportSummary } from '../types/CreditReports';

interface FullCreditReportModalProps {
  isModalOpen: boolean;
  closeModal: any;
  summary: CreditReportSummary | null;
  applicantId?: string;
  underwritingNotes?: UnderwritingNotes[];
}

const providerLabelsMap: Record<string, string> = {
  equifax: 'Equifax',
  experian: 'Experian',
  transunion: 'TransUnion',
  transunionDirect: 'TransUnion',
};

const FullCreditReportModal = (props: FullCreditReportModalProps) => {
  const { isModalOpen, closeModal, summary, applicantId, underwritingNotes } = props;
  const isDeclinedForSsnMismatch = underwritingNotes?.includes(UnderwritingNotes.ssnMismatch) ?? false;
  const [trigger, { data, error, isFetching }] = useLazyGetCreditReportDetailsQuery();

  const { id } = useParams<{ id: any }>();

  const [selectedTab, setSelectedTab] = useState<number>(0);

  const handleTabSelection = useCallback(
    (_: any, index: number) => {
      setSelectedTab(index);
    },
    [setSelectedTab],
  );

  useEffect(() => {
    if (isModalOpen && summary) {
      setSelectedTab(0);
      trigger({ accountId: id, applicationId: summary.applicationId || '', applicantId });
    }
  }, [isModalOpen, summary, trigger, id, applicantId]);
  return (
    <Modal ariaLabelledBy="titleBasic" isOpen={isModalOpen} onDismiss={closeModal}>
      <Modal.Header
        id="titleBasic"
        title={`${summary?.applicantName} - ${getFriendlyNameForReportType(summary?.reports[0]?.type)}`}
        onDismiss={closeModal}
      />
      <Modal.Body padding="0 lg lg">
        {isFetching && <div>Loading...</div>}
        {!isFetching && !error && (
          <Box childGap="sm">
            <Tabs value={selectedTab} onChange={handleTabSelection} background="primary">
              {data?.map((report: any) => (
                <Tabs.Item key={`${report.provider}-${report.id}`}>{providerLabelsMap[report.provider]}</Tabs.Item>
              ))}
            </Tabs>
            <TabPanels value={selectedTab}>
              {data?.map((report: any) => (
                <Box key={report.id}>
                  {isDeclinedForSsnMismatch && report.wasApplicantFound && (
                    <Box background="warning" padding="md" margin="0 0 sm 0" role="alert">
                      While the bureau returned a credit report, Palmetto Underwriting Policy has decisioned this as
                      a Declined Application.
                    </Box>
                  )}
                  {report.request && (
                    <Box background="tertiary" padding="sm">
                      <code>
                        Request:
                        <pre>{JSON.stringify(report.request, null, 2)}</pre>
                      </code>
                    </Box>
                  )}
                  <Box background="tertiary" padding="sm">
                    <code>
                      Response:
                      <pre>{JSON.stringify(report.response, null, 2)}</pre>
                    </code>
                  </Box>
                </Box>
              ))}
            </TabPanels>
          </Box>
        )}
        {!isFetching && error && <Box>error fetching report details</Box>}
      </Modal.Body>
    </Modal>
  );
};

export default FullCreditReportModal;
